import { PipelineRunOutcome } from 'api/admin/orders/getOrderPipelineAudit';
import type { SnapshotField } from 'api/admin/orders/getOrderPipelineAudit';

/** Shown for a value the backend did not record. Never render a missing amount as 0 or $0.00. */
export const NOT_RECORDED = '—';

/** Shown for a list that was recorded but empty — distinct from a list never recorded at all. */
export const EMPTY_LIST = 'None';

/**
 * Snapshot money fields are NOT in consistent units — this is a property of the logged payload, not
 * a choice made here. Observed values: `orderTotal` 18, `netOrderTotal` 18.9, `convenienceFee` 0.9
 * are dollars, while `taxAmountInCents` is cents. So formatting is per field, never one blanket
 * formatter across the snapshot.
 */
export const DOLLAR_FIELDS: ReadonlySet<string> = new Set<SnapshotField>([
  'orderTotal',
  'netOrderTotal',
  'balanceDue',
  'orderItemsTotal',
  'promotionAmount',
  'convenienceFee',
  'tipAmount',
  'creditAmount',
  'loyaltyAmount',
  'roundUpAmount',
]);

export const CENT_FIELDS: ReadonlySet<string> = new Set<SnapshotField>(['taxAmountInCents']);

/**
 * Money-valued keys that appear in UoW details but are not snapshot fields. Listed explicitly rather
 * than inferred from the name: guessing which keys are money — and in which unit — would eventually
 * print a wrong figure in a tool used to settle financial disputes.
 */
export const DETAIL_DOLLAR_KEYS: ReadonlySet<string> = new Set(['previousBalanceDue', 'newBalanceDue']);

export const CENTS_IN_A_DOLLAR = 100;

export const SNAPSHOT_FIELD_LABELS: Record<string, string | undefined> = {
  status: 'Status',
  orderTotal: 'Order total',
  netOrderTotal: 'Net order total',
  balanceDue: 'Balance due',
  orderItemsTotal: 'Order items total',
  promotionAmount: 'Promotion',
  taxAmountInCents: 'Tax',
  convenienceFee: 'Convenience fee',
  tipAmount: 'Tip',
  creditAmount: 'Credit',
  loyaltyAmount: 'Loyalty',
  roundUpAmount: 'Round up',
  promotionIds: 'Promotion IDs',
  lineItemCount: 'Line items',
  /*
   * Indexed by arbitrary log keys, so the declared type allows any string and admits `undefined` —
   * callers must handle a miss. `satisfies` still enforces the real contract: every SnapshotField is
   * present, no bogus key sneaks in, and every label is a string.
   */
} satisfies Record<SnapshotField, string>;

type ChipColor = 'success' | 'error' | 'warning' | 'default';

/**
 * POST_COMMIT_FAILURE is intentionally not styled like a rollback. It means the transaction
 * COMMITTED — the DB change is real — but post-commit work failed, so downstream side effects may be
 * missing. Making it look like a generic failure would hide the most important signal here.
 */
export const OUTCOME_DISPLAY: Record<PipelineRunOutcome, { label: string; color: ChipColor }> = {
  [PipelineRunOutcome.COMMITTED]: { label: 'Committed', color: 'success' },
  [PipelineRunOutcome.ROLLED_BACK]: { label: 'Rolled back', color: 'error' },
  [PipelineRunOutcome.POST_COMMIT_FAILURE]: {
    label: 'Committed, post-commit failure',
    color: 'warning',
  },
  [PipelineRunOutcome.INCOMPLETE]: { label: 'No end recorded', color: 'default' },
};
